/**
 * glitchPipeline — chromatic glitch with horizontal slices.
 *
 * Horizontal slices of the screen are randomly shifted horizontally
 * and have their RGB channels split. Combined with the existing
 * FBM image, this creates a classic datamosh look. Audio drives the
 * glitch intensity.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface GlitchPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

vec3 sampleImage(vec2 p) {
  // Generate a colorful "image" via FBM.
  vec2 ip = p * 2.0;
  vec3 col1 = vec3(0.95, 0.30, 0.20);
  vec3 col2 = vec3(0.30, 0.65, 0.95);
  vec3 col3 = vec3(0.95, 0.85, 0.30);
  vec3 col4 = vec3(0.45, 0.25, 0.85);
  vec3 img = mix(col1, col2, fbm(ip + uTime * 0.2));
  img = mix(img, col3, fbm(ip * 2.0 - uTime * 0.3));
  img = mix(img, col4, fbm(ip * 3.0 + uTime * 0.15));
  return img;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Determine which horizontal slice we're in.
  float sliceSize = 0.04 + bass * 0.04;
  float sliceIdx = floor(p.y / sliceSize);
  // Random offset for each slice.
  float sliceRand = hash21(vec2(sliceIdx, floor(uTime * 8.0)));
  // Displacement: only on slices where random > threshold.
  float threshold = 0.6 - bass * 0.3;
  float sliceOffset = step(threshold, sliceRand) * (sliceRand - 0.5) * 0.4 * (1.0 + bass);
  vec2 sliceP = p + vec2(sliceOffset, 0.0);

  // RGB split: each channel shifted by different amount.
  float chromaSplit = 0.02 + high * 0.03;
  vec3 col;
  col.r = sampleImage(sliceP + vec2(chromaSplit, 0.0)).r;
  col.g = sampleImage(sliceP).g;
  col.b = sampleImage(sliceP - vec2(chromaSplit, 0.0)).b;

  // Periodic block-corruption: small rectangles become black/white.
  float blockRand = hash21(vec2(floor(p.x * 20.0), floor(p.y * 20.0) + floor(uTime * 4.0)));
  if (blockRand > 0.95 + mid * 0.04) {
    col = mix(vec3(0.0), vec3(1.0), step(0.5, blockRand));
  }

  // Scan lines (subtle CRT effect).
  float scan = 0.85 + 0.15 * sin(gl_FragCoord.y * 3.0);
  col *= scan;

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeGlitchBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.7);
  out[1] = 0.5 + 0.3 * Math.sin(t * 1.0 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.3 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.5 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.7 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 2.0 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeGlitchRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createGlitchPipeline(gl: WebGL2RenderingContext): GlitchPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawGlitch(
  gl: WebGL2RenderingContext,
  pipe: GlitchPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
